import { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Progress } from '../../ui/progress';
import { cn } from '../../../lib/utils';
import { Plus, ChevronRight, CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SectionHeader = ({ title, label, labelColor, description }) => (
  <div className="flex items-start justify-between">
    <div>
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {label && <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", labelColor)}>{label}</Badge>}
      </div>
      {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
    </div>
  </div>
);

const FormField = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-slate-600">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
  </div>
);

const EMPTY_FORM = {
  first_name: '', middle_name: '', last_name: '', date_of_birth: '', gender: '',
  phone: '', email: '',
  street: '', city: '', state: '', zip_code: '',
  payer_name: '', member_id: '', group_number: '',
};

const AddPatientModal = ({ open, onOpenChange, onSuccess, prefill }) => {
  const { authFetch } = useAuth();
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) setForm(prefill ? { ...EMPTY_FORM, ...prefill } : { ...EMPTY_FORM });
    setErrors({});
  }, [open, prefill]);

  const set = (field, val) => {
    setForm(f => ({ ...f, [field]: val }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: '' }));
  };

  const completedSections = [
    form.first_name && form.last_name && form.date_of_birth,
    form.phone || form.email,
    form.street || form.city,
    form.payer_name || form.member_id,
  ].filter(Boolean).length;

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'Required';
    if (!form.last_name.trim()) e.last_name = 'Required';
    if (!form.date_of_birth.trim()) e.date_of_birth = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await authFetch(`${API}/api/patients`, {
        method: 'POST',
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(`Patient ${form.first_name} ${form.last_name} created`);
        onOpenChange(false);
        onSuccess?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create patient');
      }
    } catch {
      toast.error('Network error creating patient');
    }
    setSubmitting(false);
  };

  const inputCls = "h-10 text-sm border-slate-200 focus-visible:ring-slate-400 focus-visible:border-slate-400";
  const errorCls = "ring-1 ring-red-300 border-red-300";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-patient-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Add New Patient</DialogTitle>
          <DialogDescription>Complete the sections below to register a new patient</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          {/* Scan ID Card shortcut */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
            data-testid="add-patient-scan-shortcut">
            <div className="flex items-center gap-2.5">
              <CreditCard className="h-4 w-4 text-slate-500" />
              <div>
                <p className="text-xs font-medium text-slate-700">Scan ID Card</p>
                <p className="text-[10px] text-slate-500">Auto-fill from driver's license or insurance card</p>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-400" />
          </div>

          {/* Form Progress */}
          <div data-testid="add-patient-progress">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-slate-600">Form Progress</span>
              <span className="text-xs text-slate-500">{completedSections} of 4 sections complete</span>
            </div>
            <Progress value={(completedSections / 4) * 100} className="h-1.5" />
          </div>

          {/* Section 1: Personal Information */}
          <div className="space-y-3" data-testid="section-personal-info">
            <SectionHeader title="Personal Information" label="Required" labelColor="text-red-600 border-red-200" description="Name, date of birth, and gender" />
            <div className="grid grid-cols-3 gap-3">
              <FormField label="First Name" required>
                <Input value={form.first_name} onChange={e => set('first_name', e.target.value)}
                  className={cn(inputCls, errors.first_name && errorCls)} data-testid="input-first-name" />
              </FormField>
              <FormField label="Middle Name">
                <Input value={form.middle_name} onChange={e => set('middle_name', e.target.value)}
                  className={inputCls} data-testid="input-middle-name" />
              </FormField>
              <FormField label="Last Name" required>
                <Input value={form.last_name} onChange={e => set('last_name', e.target.value)}
                  className={cn(inputCls, errors.last_name && errorCls)} data-testid="input-last-name" />
              </FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Date of Birth" required>
                <Input type="date" value={form.date_of_birth} onChange={e => set('date_of_birth', e.target.value)}
                  placeholder="mm/dd/yyyy" className={cn(inputCls, errors.date_of_birth && errorCls)} data-testid="input-dob" />
              </FormField>
              <FormField label="Gender">
                <Select value={form.gender} onValueChange={v => set('gender', v)}>
                  <SelectTrigger className={cn(inputCls, "w-full")} data-testid="select-gender">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                    <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </div>
          </div>

          {/* Section 2: Contact Information */}
          <div className="space-y-3" data-testid="section-contact-info">
            <SectionHeader title="Contact Information" description="Phone number and email address" />
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Phone">
                <Input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)}
                  className={inputCls} data-testid="input-phone" />
              </FormField>
              <FormField label="Email">
                <Input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                  className={inputCls} data-testid="input-email" />
              </FormField>
            </div>
          </div>

          {/* Section 3: Address */}
          <div className="space-y-3" data-testid="section-address">
            <SectionHeader title="Address" description="Residential address with quick search" />
            <FormField label="Quick Address Lookup">
              <Input placeholder="Type to search... (e.g., 123 Main St, New York)"
                className={inputCls} data-testid="input-address-lookup" />
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Street">
                <Input value={form.street} onChange={e => set('street', e.target.value)}
                  className={inputCls} data-testid="input-street" />
              </FormField>
              <FormField label="City">
                <Input value={form.city} onChange={e => set('city', e.target.value)}
                  className={inputCls} data-testid="input-city" />
              </FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="State">
                <Input value={form.state} onChange={e => set('state', e.target.value)}
                  className={inputCls} data-testid="input-state" />
              </FormField>
              <FormField label="ZIP Code">
                <Input value={form.zip_code} onChange={e => set('zip_code', e.target.value)}
                  className={inputCls} data-testid="input-zip" />
              </FormField>
            </div>
          </div>

          {/* Section 4: Insurance Information */}
          <div className="space-y-3" data-testid="section-insurance-info">
            <SectionHeader title="Insurance Information" label="Optional" labelColor="text-slate-500 border-slate-200" description="Primary insurance details" />
            <FormField label="Payer Name">
              <Input value={form.payer_name} onChange={e => set('payer_name', e.target.value)}
                className={inputCls} data-testid="input-payer-name" />
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Member ID">
                <Input value={form.member_id} onChange={e => set('member_id', e.target.value)}
                  className={inputCls} data-testid="input-member-id" />
              </FormField>
              <FormField label="Group Number">
                <Input value={form.group_number} onChange={e => set('group_number', e.target.value)}
                  className={inputCls} data-testid="input-group-number" />
              </FormField>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 flex items-center justify-between">
          <p className="text-[11px] text-slate-400">* Required fields</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-add-patient">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}
              className="bg-black hover:bg-gray-800 gap-2" data-testid="submit-new-patient-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Create Patient</>}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddPatientModal;
